import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  DpNoticeType,
  DpTerminationType,
  NOTICE_TYPE_LABELS,
  openTerminationApi,
  previewTerminationApi,
  TERMINATION_TYPE_LABELS,
  TERMINATIONS_WITHOUT_NOTICE,
  TerminationPayload,
  TerminationPreview,
} from "../api/processesApi";
import type { DpEmployeeContract } from "../api/contractsApi";
import { formatDateOnly } from "../utils/format";
import { probationStateOf } from "../utils/probation";

type Props = {
  token: string;
  employeeId: string;
  contract: DpEmployeeContract;
  onClose: () => void;
  onOpened: (processId: string) => void;
};

/**
 * Abertura do desligamento. A projeção da data de término (aviso proporcional,
 * 30 + 3 por ano) vem do backend a cada mudança — a regra existe num lugar só.
 * Nada é gravado como rescisão aqui: ela só nasce na conclusão do checklist.
 */
export default function TerminationFormModal({ token, employeeId, contract, onClose, onOpened }: Props) {
  // Em experiência, o caso comum é encerrar no fim do prazo: o formulário já vem assim.
  const inProbation = ["PRIMEIRO_PERIODO", "PRORROGACAO"].includes(probationStateOf(contract));
  const [form, setForm] = useState({
    terminationType: (inProbation ? "TERMINO_CONTRATO" : "SEM_JUSTA_CAUSA") as DpTerminationType,
    noticeType: (inProbation ? "NAO_APLICAVEL" : "TRABALHADO") as DpNoticeType,
    noticeStartDate: "",
    lastWorkingDay: inProbation ? contract.probationEndDate?.slice(0, 10) ?? "" : "",
    terminationDate: "",
    reason: "",
  });
  const [preview, setPreview] = useState<TerminationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const withoutNotice = TERMINATIONS_WITHOUT_NOTICE.includes(form.terminationType);
  const noticeOptions: DpNoticeType[] = withoutNotice ? ["NAO_APLICAVEL"] : ["TRABALHADO", "INDENIZADO", "DISPENSADO"];

  // Mantém o aviso coerente com o tipo escolhido.
  useEffect(() => {
    if (!noticeOptions.includes(form.noticeType)) {
      setForm((f) => ({ ...f, noticeType: noticeOptions[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.terminationType]);

  const payload = (): TerminationPayload => ({
    contractId: contract.id,
    terminationType: form.terminationType,
    noticeType: form.noticeType,
    noticeStartDate: form.noticeType === "TRABALHADO" ? form.noticeStartDate || null : null,
    lastWorkingDay: form.lastWorkingDay,
    terminationDate: form.terminationDate || null,
    reason: form.reason.trim() || null,
  });

  useEffect(() => {
    if (!form.lastWorkingDay || (form.noticeType === "TRABALHADO" && !form.noticeStartDate)) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      previewTerminationApi(token, employeeId, payload())
        .then((result) => {
          if (cancelled) return;
          setPreview(result);
          setPreviewError(null);
        })
        .catch((error: any) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(error?.message || "Não foi possível projetar.");
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, employeeId, form]);

  const submit = async () => {
    if (!form.lastWorkingDay) return toast.error("Informe o último dia trabalhado.");
    if (form.noticeType === "TRABALHADO" && !form.noticeStartDate) {
      return toast.error("Informe o início do aviso.");
    }
    setSaving(true);
    try {
      const opened = await openTerminationApi(token, employeeId, payload());
      opened.warnings?.forEach((warning) => toast.warning(warning));
      toast.success("Desligamento iniciado.");
      onOpened(opened.id);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao iniciar o desligamento.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">Iniciar desligamento</h2>
          <button className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>

        <div className="dpModal__body">
          <p className="dpNotice">
            Contrato admitido em {formatDateOnly(contract.admissionDate)}. A rescisão só é registrada
            quando o checklist for concluído — até lá, dá para cancelar. Ao iniciar, o sistema já
            calcula o demonstrativo de rescisão (prévia), que é recalculado na conclusão.
          </p>
          {inProbation ? (
            <p className="dpNotice dpNotice--warning">
              Contrato em experiência até {formatDateOnly(contract.probationEndDate)}
              {contract.probationFirstEndDate
                ? ` (1º período até ${formatDateOnly(contract.probationFirstEndDate)})`
                : ""}
              . No fim do prazo, use o término do contrato; antes dele, a rescisão antecipada pela
              empresa ou pelo colaborador.
            </p>
          ) : null}

          <div className="dpFormGrid">
            <div className="dpFormGroup">
              <label htmlFor="termType">Tipo de desligamento *</label>
              <select
                id="termType"
                className="dpSelect"
                value={form.terminationType}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, terminationType: e.target.value as DpTerminationType }))}
              >
                {(Object.keys(TERMINATION_TYPE_LABELS) as DpTerminationType[]).map((t) => (
                  <option key={t} value={t}>
                    {TERMINATION_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div className="dpFormGroup">
              <label htmlFor="termNotice">Aviso prévio *</label>
              <select
                id="termNotice"
                className="dpSelect"
                value={form.noticeType}
                disabled={saving || withoutNotice}
                onChange={(e) => setForm((f) => ({ ...f, noticeType: e.target.value as DpNoticeType }))}
              >
                {noticeOptions.map((n) => (
                  <option key={n} value={n}>
                    {NOTICE_TYPE_LABELS[n]}
                  </option>
                ))}
              </select>
            </div>

            {form.noticeType === "TRABALHADO" ? (
              <div className="dpFormGroup">
                <label htmlFor="termNoticeStart">Início do aviso *</label>
                <input
                  id="termNoticeStart"
                  type="date"
                  className="dpInput"
                  value={form.noticeStartDate}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, noticeStartDate: e.target.value }))}
                />
              </div>
            ) : null}

            <div className="dpFormGroup">
              <label htmlFor="termLastDay">Último dia trabalhado *</label>
              <input
                id="termLastDay"
                type="date"
                className="dpInput"
                value={form.lastWorkingDay}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, lastWorkingDay: e.target.value }))}
              />
            </div>

            <div className="dpFormGroup">
              <label htmlFor="termDate">Data de término</label>
              <input
                id="termDate"
                type="date"
                className="dpInput"
                value={form.terminationDate}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, terminationDate: e.target.value }))}
              />
              <span className="dpFormHint">Vazio = a projeção do sistema.</span>
            </div>
          </div>

          {preview ? (
            <div className="dpNotice" style={{ marginTop: 8 }}>
              <strong>Projeção:</strong> término em {formatDateOnly(preview.projectedTerminationDate)}
              {preview.noticeDays !== null ? ` (${preview.noticeDays} dias de aviso)` : ""}.
              {preview.warnings.map((warning) => (
                <div key={warning} style={{ color: "#8a4b08", marginTop: 4 }}>
                  ⚠ {warning}
                </div>
              ))}
            </div>
          ) : null}
          {previewError ? (
            <div className="dpNotice dpNotice--warning" style={{ marginTop: 8 }}>
              {previewError}
            </div>
          ) : null}

          <div className="dpFormGroup">
            <label htmlFor="termReason">Motivo / observações</label>
            <textarea
              id="termReason"
              className="dpTextarea"
              maxLength={1000}
              value={form.reason}
              disabled={saving}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            />
          </div>
        </div>

        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="button"
            className="dpBtn dpBtn--danger"
            onClick={submit}
            disabled={saving || Boolean(previewError)}
          >
            {saving ? "Iniciando..." : "Iniciar desligamento"}
          </button>
        </footer>
      </div>
    </div>
  );
}
